/* RESPONSIBLE TEAM: team-phone */
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';
import type Store from '@ember-data/store';
import type { FinVoiceNumberConfigurationHandoverConfiguration } from 'embercom/models/fin-voice/number-configuration';
import type { FinVoiceVoice } from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import { FIN_VOICE_11LABS_VOICES } from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import type PhoneNumber from 'embercom/models/calling-phone-number';

export type OfficeHoursScheduleOption = {
  value: string;
  text: string;
  description: string;
  isDefault: boolean;
};

export interface Schedule {
  id: string;
  name: string;
  labelWithTimezone: string;
  isNew: boolean;
  isDefault: boolean;
}

export default class FinVoiceConfigService extends Service {
  @service declare appService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare store: Store;

  @tracked numberConfigurations: Record<string, FinVoiceNumberConfiguration> = {};
  @tracked officeHoursSchedules: OfficeHoursScheduleOption[] = [];
  @tracked phoneNumbers: Record<string, PhoneNumber> = {};
  @tracked numberConfigurationsLoaded = false;
  @tracked voiceOptions: { value: FinVoiceVoice; text: string; description: string }[] = [];

  constructor(owner: object) {
    super(owner);

    this.voiceOptions = FIN_VOICE_11LABS_VOICES.map((voice) => ({
      value: voice.value,
      text: voice.text,
      description: voice.description.map((desc) => this.intl.t(desc)).join(' '),
    }));
  }

  getDefaultOfficeHoursSchedule() {
    return this.officeHoursSchedules.find((schedule) => schedule.isDefault)?.value;
  }

  _formatNumberConfigurations(
    numberConfigurations: FinVoiceNumberConfiguration[],
    phoneNumbers: PhoneNumber[],
  ) {
    return phoneNumbers.reduce(
      (acc, phoneNumber) => {
        let numberConfiguration = numberConfigurations.find(
          (config) => config.workspacePhoneNumber === phoneNumber.phoneNumber,
        );

        if (numberConfiguration) {
          acc[phoneNumber.phoneNumber] = numberConfiguration;
        } else if (phoneNumber.inboundEnabled) {
          acc[phoneNumber.phoneNumber] = this.addNewNumberConfiguration(
            phoneNumber.phoneNumber,
            false,
          );
        }

        return acc;
      },
      {} as Record<string, FinVoiceNumberConfiguration>,
    );
  }

  async loadAllData() {
    if (this.numberConfigurationsLoaded) {
      return;
    }

    let [officeHoursSchedules, phoneNumbers, numberConfigurations] = await Promise.all([
      this.fetchOfficeHoursSchedules(),
      this.fetchPhoneNumbers(),
      this.fetchConfigurations(),
    ]);

    this.officeHoursSchedules = officeHoursSchedules;
    this.phoneNumbers = phoneNumbers.reduce(
      (acc, phoneNumber) => {
        acc[phoneNumber.phoneNumber] = phoneNumber;
        return acc;
      },
      {} as Record<string, PhoneNumber>,
    );

    this.numberConfigurations = this._formatNumberConfigurations(
      numberConfigurations,
      phoneNumbers,
    );

    this.numberConfigurationsLoaded = true;
  }

  async fetchConfigurations() {
    return (await this.store.findAll('fin-voice/number-configuration')).toArray();
  }

  async fetchOfficeHoursSchedules() {
    let officeHoursSchedules: Schedule[] = (
      await this.store.findAll('office-hours-schedule')
    ).toArray();

    return officeHoursSchedules.map((schedule) => ({
      value: schedule.id,
      text: schedule.name,
      description: schedule.labelWithTimezone,
      isDefault: schedule.isDefault,
    }));
  }

  async fetchPhoneNumbers() {
    if (Object.keys(this.phoneNumbers).length) {
      return Object.values(this.phoneNumbers);
    }

    let phoneNumbers = await this.store.findAll('calling-phone-number');

    return phoneNumbers.toArray().filter((phoneNumber) => phoneNumber.status === 'active');
  }

  addNewNumberConfiguration(newPhoneNumber: string, addToNumberConfigurations = true) {
    let newNumberConfiguration: FinVoiceNumberConfiguration = this.store.createRecord(
      'fin-voice/number-configuration',
      {
        appId: this.appService.app.id,
        workspacePhoneNumber: newPhoneNumber,
        predicates: {},
        handoverConfiguration: {
          in: {
            type: 'handover-to-voicemail',
            config: {},
          },
          nin: {
            type: 'handover-to-voicemail',
            config: {},
          },
        },
        status: 'draft',
      },
    );

    if (addToNumberConfigurations) {
      this.numberConfigurations[newPhoneNumber] = newNumberConfiguration;
    }

    return newNumberConfiguration;
  }

  async updatePercentageRollout(
    phoneNumber: string,
    rollOutPercentageIn: number | undefined,
    rollOutPercentageNin: number | undefined,
  ) {
    let numberConfiguration = this.numberConfigurations[phoneNumber];

    if (!numberConfiguration) {
      numberConfiguration = this.addNewNumberConfiguration(phoneNumber);
    }

    numberConfiguration.predicates = {
      officeHoursSchedule: numberConfiguration.predicates?.officeHoursSchedule,
      in: {
        percentageRollout: rollOutPercentageIn || 0,
      },
      nin: {
        percentageRollout: rollOutPercentageNin || 0,
      },
    };

    numberConfiguration.status = !rollOutPercentageIn && !rollOutPercentageNin ? 'draft' : 'active';

    await numberConfiguration.save();
  }

  async addAndSaveNewOfficeHoursSchedule(phoneNumber: string, schedule: Schedule) {
    this.officeHoursSchedules.push({
      value: schedule.id,
      text: schedule.name,
      description: schedule.labelWithTimezone,
      isDefault: schedule.isDefault,
    });

    await this.updateOfficeHoursSchedule(phoneNumber, schedule.id);
  }

  async updateOfficeHoursSchedule(phoneNumber: string, officeHoursSchedule: string) {
    let numberConfiguration = this.numberConfigurations[phoneNumber];

    if (!numberConfiguration) {
      numberConfiguration = this.addNewNumberConfiguration(phoneNumber);
    }

    numberConfiguration.predicates.officeHoursSchedule = officeHoursSchedule;

    await numberConfiguration.save();
  }

  async updateVoiceBehavior(phoneNumber: string, voice: FinVoiceVoice, greeting: string) {
    let numberConfiguration = this.numberConfigurations[phoneNumber];

    if (!numberConfiguration) {
      numberConfiguration = this.addNewNumberConfiguration(phoneNumber);
    }

    numberConfiguration.voice = voice;
    numberConfiguration.greeting = greeting;

    await numberConfiguration.save();
  }

  async updateHandoverConfiguration(
    phoneNumber: string,
    handoverConfiguration: FinVoiceNumberConfigurationHandoverConfiguration,
  ) {
    let numberConfiguration = this.numberConfigurations[phoneNumber];

    if (!numberConfiguration) {
      numberConfiguration = this.addNewNumberConfiguration(phoneNumber);
    }

    numberConfiguration.handoverConfiguration = handoverConfiguration;
    await numberConfiguration.save();
  }
}

declare module '@ember/service' {
  interface Registry {
    finVoiceConfigService: FinVoiceConfigService;
    'fin-voice-config-service': FinVoiceConfigService;
  }
}
